import { Box } from "@material-ui/core"
import { useState } from "react"
import type { ReactElement } from "react"
import { Controller } from "react-hook-form"

import type { Control, UseFormGetValues, UseFormSetValue, UseFormWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import type { FormAttachment } from ".."

import { AttachmentType } from "../../../models"
import { Header4, TextInputField } from "../../atoms"
import { AttachmentTypeSelector, MediaInputField } from "../../molecules"

import "./attachment-data-form.styles.scss"

export interface IAttachmentDataForm {
  control: Control<FormAttachment>
  watch: UseFormWatch<FormAttachment>
  valid: boolean
  isCreating: boolean
  changeType: (type: AttachmentType) => void
  onSelectFile: (file: File) => void
  setValid: (valid: boolean) => void
  setValue: UseFormSetValue<FormAttachment>
  getValues: UseFormGetValues<FormAttachment>
}

export function AttachmentDataForm(props: IAttachmentDataForm): ReactElement {
  const { changeType, valid, control, isCreating, watch, onSelectFile, setValid, setValue, getValues } = props
  const { t } = useTranslation("map")

  const [filePreview, setFilePreview] = useState<string>()

  const attachmentType: AttachmentType | null = watch().type
  const location: string | undefined = watch().location
  const description: string = watch().description

  function handleFileSelect(file: File | null): void {
    if (file !== null) {
      setFilePreview(URL.createObjectURL(file))
      onSelectFile(file)
    } else {
      if (filePreview) URL.revokeObjectURL(filePreview)
      setFilePreview(undefined)
    }
  }

  return (
    <Box className="attachment-data-form">
      <Header4>{t("attachments.addSpecificAttachment")}</Header4>
      <form className="attachment-data-form-content">
        <AttachmentTypeSelector attachmentType={attachmentType} disabled={!isCreating} onSelect={changeType} />
        <Controller control={control} name="type" rules={{ required: true }} render={() => <input type="hidden" />} />
        {attachmentType !== null && (
          <Box className="attachment-data-form-inputs">
            {attachmentType === AttachmentType.Image && (
              <MediaInputField
                remoteUrl={location}
                name="location"
                control={control}
                label={t("attachments.image.urlImage")}
                placeholder={t("attachments.image.enterUrl")}
                fullWidth
                required
                valid={valid}
                maxFileSizeInMb={10}
                type={AttachmentType.Image}
                blob={filePreview}
                onSelect={handleFileSelect}
                onValidChanged={setValid}
                setValue={setValue}
                getValues={getValues}
              />
            )}
            {attachmentType === AttachmentType.Video && (
              <MediaInputField
                remoteUrl={location}
                name="location"
                control={control}
                label={t("attachments.video.urlVideo")}
                placeholder={t("attachments.video.enterUrl")}
                fullWidth
                required
                valid={valid}
                maxFileSizeInMb={10}
                type={AttachmentType.Video}
                blob={filePreview}
                onSelect={handleFileSelect}
                onValidChanged={setValid}
                setValue={setValue}
                getValues={getValues}
              />
            )}
            <TextInputField
              name="title"
              label={t("attachments.title")}
              placeholder={t("attachments.enterTitle")}
              control={control}
              fullWidth
            />
            <TextInputField
              name="description"
              label={
                attachmentType === AttachmentType.Note ? t("attachments.note.noteTitle") : t("attachments.description")
              }
              placeholder={
                attachmentType === AttachmentType.Note
                  ? t("attachments.note.typeNote")
                  : t("attachments.enterDescription")
              }
              control={control}
              fullWidth
              multiline
              rows={3}
              maxLength={150}
              subLabel={`${description.length}/150`}
            />
          </Box>
        )}
      </form>
    </Box>
  )
}
